import type {
  CustomerDTO,
  CustomerDetailDTO,
  CustomerGroupDTO,
  CustomerGroupMemberDTO,
  MemberTierDTO,
} from "@/modules/customers/domain/contracts";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type {
  CustomerDTO,
  CustomerDetailDTO,
  CustomerGroupDTO,
  CustomerGroupMemberDTO,
  MemberTierDTO,
};

export const customersApi = {
  async list(
    query: OffsetPageQueryDTO,
  ): Promise<OffsetPageDTO<CustomerDTO>> {
    const url = new URL(`${config.apiUrl}/customers`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<CustomerDTO>>(url.toString());
  },

  async get(id: string): Promise<CustomerDetailDTO> {
    return fetcher.get<CustomerDetailDTO>(`${config.apiUrl}/customers/${id}`);
  },

  async update(
    id: string,
    data: { memberTierId?: string | null; metadata?: Record<string, unknown> | null },
  ): Promise<CustomerDetailDTO> {
    return fetcher.put<CustomerDetailDTO>(
      `${config.apiUrl}/customers/${id}`,
      data,
    );
  },
};

export const memberTiersApi = {
  async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<MemberTierDTO>> {
    const url = new URL(`${config.apiUrl}/customers/tiers`);
    url.searchParams.set("limit", String(query.limit ?? 100));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<MemberTierDTO>>(url.toString());
  },

  async get(id: string): Promise<MemberTierDTO> {
    return fetcher.get<MemberTierDTO>(
      `${config.apiUrl}/customers/tiers/${id}`,
    );
  },

  async create(data: {
    name: { lo?: string; th?: string; en?: string };
    sortOrder: number;
  }): Promise<MemberTierDTO> {
    return fetcher.post<MemberTierDTO>(
      `${config.apiUrl}/customers/tiers`,
      data,
    );
  },

  async update(
    id: string,
    data: {
      name?: { lo?: string; th?: string; en?: string };
      sortOrder?: number;
    },
  ): Promise<MemberTierDTO> {
    return fetcher.put<MemberTierDTO>(
      `${config.apiUrl}/customers/tiers/${id}`,
      data,
    );
  },

  async delete(id: string): Promise<{ id: string }> {
    return fetcher.delete<{ id: string }>(
      `${config.apiUrl}/customers/tiers/${id}`,
    );
  },
};

export const customerGroupsApi = {
  async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<CustomerGroupDTO>> {
    const url = new URL(`${config.apiUrl}/customers/groups`);
    url.searchParams.set("limit", String(query.limit ?? 100));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<CustomerGroupDTO>>(url.toString());
  },

  async get(id: string): Promise<CustomerGroupDTO> {
    return fetcher.get<CustomerGroupDTO>(
      `${config.apiUrl}/customers/groups/${id}`,
    );
  },

  async create(data: {
    name: { lo?: string; th?: string; en?: string };
    description?: string;
    color?: string;
    sortOrder?: number;
  }): Promise<CustomerGroupDTO> {
    return fetcher.post<CustomerGroupDTO>(
      `${config.apiUrl}/customers/groups`,
      data,
    );
  },

  async update(
    id: string,
    data: {
      name?: { lo?: string; th?: string; en?: string };
      description?: string | null;
      color?: string | null;
      sortOrder?: number;
    },
  ): Promise<CustomerGroupDTO> {
    return fetcher.put<CustomerGroupDTO>(
      `${config.apiUrl}/customers/groups/${id}`,
      data,
    );
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return fetcher.delete<{ success: boolean }>(
      `${config.apiUrl}/customers/groups/${id}`,
    );
  },

  async listForCustomer(customerId: string): Promise<CustomerGroupMemberDTO[]> {
    return fetcher.get<CustomerGroupMemberDTO[]>(
      `${config.apiUrl}/customers/${customerId}/groups`,
    );
  },

  async assignCustomer(
    customerId: string,
    groupId: string,
  ): Promise<{ success: boolean }> {
    return fetcher.post<{ success: boolean }>(
      `${config.apiUrl}/customers/${customerId}/groups`,
      { groupId },
    );
  },

  async removeCustomer(
    customerId: string,
    groupId: string,
  ): Promise<{ success: boolean }> {
    return fetcher.delete<{ success: boolean }>(
      `${config.apiUrl}/customers/${customerId}/groups/${groupId}`,
    );
  },
};
